import { oc } from "@orpc/contract";
import { WAREHOUSE_TYPE_VALUES } from "@erp/shared-types";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const WarehouseAddressSchema = z.object({
  id: z.string().uuid(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  locality: z.string(),
  administrativeArea: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryIso31661Alpha2: z.string(),
});

export const WarehouseSectionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  status: z.string(),
  capacity: z.record(z.string(), z.unknown()).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export const WarehouseTypeSchema = z.enum(WAREHOUSE_TYPE_VALUES);

export const WarehouseDivisionSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const WarehouseDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  address: WarehouseAddressSchema.nullable(),
  type: WarehouseTypeSchema.nullable(),
  division: WarehouseDivisionSummarySchema.nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  sectionCount: z.number(),
  sections: z.array(WarehouseSectionSchema).optional(),
});

export const WarehouseSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
});

// ─── Create ─────────────────────────────────────────────────────────────────

export const CreateWarehouseAddressInputSchema = z.object({
  street: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  zipCode: z.string().min(1),
  country: z.string().min(1),
});

export const CreateWarehouseInputSchema = z.object({
  name: z.string().min(1).max(255),
  description: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v)),
  address: CreateWarehouseAddressInputSchema.nullable().optional(),
  type: WarehouseTypeSchema.nullable().optional(),
  divisionId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

// ─── List ───────────────────────────────────────────────────────────────────

export const ListWarehousesInputSchema = z.object({
  includeSections: z.coerce.boolean().optional().default(false),
});

export const ListWarehousesResponseSchema = z.object({
  warehouses: z.array(WarehouseDTOSchema),
});

// ─── Update / delete ────────────────────────────────────────────────────────

export const UpdateWarehouseInputSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255).optional(),
    description: z
      .string()
      .nullable()
      .optional()
      .transform((v) => (v === "" ? null : v)),
    type: WarehouseTypeSchema.nullable().optional(),
    divisionId: z.string().uuid().nullable().optional(),
  })
  .refine(
    (input) =>
      input.name !== undefined ||
      input.description !== undefined ||
      input.type !== undefined ||
      input.divisionId !== undefined,
    {
      message:
        "At least one of name, description, type, or divisionId must be provided.",
    },
  );

export const DeleteWarehouseInputSchema = z.object({
  id: z.string().uuid(),
});

export const DeleteWarehouseResponseSchema = z.object({
  success: z.boolean(),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const warehousesContracts = {
  list: oc
    .input(ListWarehousesInputSchema)
    .output(ListWarehousesResponseSchema)
    .route({ method: "GET", summary: "List warehouses for the current org" }),
  create: oc
    .input(CreateWarehouseInputSchema)
    .output(WarehouseSummarySchema)
    .route({ summary: "Create a warehouse (optionally with an address)" }),
  update: oc
    .input(UpdateWarehouseInputSchema)
    .output(WarehouseSummarySchema)
    .route({
      summary: "Update a warehouse (name, description, type, division)",
    }),
  delete: oc
    .input(DeleteWarehouseInputSchema)
    .output(DeleteWarehouseResponseSchema)
    .route({ summary: "Soft-delete a warehouse" }),
} as const;

// ─── Types ──────────────────────────────────────────────────────────────────

export type WarehouseDTO = z.infer<typeof WarehouseDTOSchema>;
export type WarehouseAddress = z.infer<typeof WarehouseAddressSchema>;
export type WarehouseSection = z.infer<typeof WarehouseSectionSchema>;
export type WarehouseSummary = z.infer<typeof WarehouseSummarySchema>;
export type WarehouseType = z.infer<typeof WarehouseTypeSchema>;
export type WarehouseDivisionSummary = z.infer<
  typeof WarehouseDivisionSummarySchema
>;
export type CreateWarehouseInput = z.infer<typeof CreateWarehouseInputSchema>;
export type CreateWarehouseAddressInput = z.infer<
  typeof CreateWarehouseAddressInputSchema
>;
export type ListWarehousesInput = z.infer<typeof ListWarehousesInputSchema>;
export type ListWarehousesResponse = z.infer<
  typeof ListWarehousesResponseSchema
>;
export type UpdateWarehouseInput = z.infer<typeof UpdateWarehouseInputSchema>;
export type DeleteWarehouseInput = z.infer<typeof DeleteWarehouseInputSchema>;
